import {
  TimeviewBucketDisplay,
  TimeviewBucketMode,
  TimeviewViewType,
  timeviewBucketModes,
  timeviewViewTypes,
} from "types";

export const DEFAULT_TIMEVIEW_VIEW_TYPE: TimeviewViewType = "percent";
export const DEFAULT_TIMEVIEW_BUCKET_MODE: TimeviewBucketMode = "daily";

type ParseTimeviewBucketDisplayResult = {
  viewType: TimeviewViewType;
  bucketMode: TimeviewBucketMode;
};

/**
 * Narrows an unknown value to a `TimeviewViewType`.
 */
const isTimeviewViewType = (x: unknown): x is TimeviewViewType => timeviewViewTypes.includes(x as TimeviewViewType);

/**
 * Narrows an unknown value to a `TimeviewBucketMode`.
 */
const isTimeviewBucketMode = (x: unknown): x is TimeviewBucketMode =>
  timeviewBucketModes.includes(x as TimeviewBucketMode);

/**
 * Combines a `TimeviewViewType` and a `TimeviewBucketMode` into a single string.
 * This can be used as a "value" for dropdowns, for example.
 *
 * See `parseTimeviewBucketDisplay` for the reverse of this operation.
 */
export const encodeTimeviewBucketDisplay = (
  type: TimeviewViewType,
  bucket: TimeviewBucketMode,
): TimeviewBucketDisplay => `${type}-${bucket}`;

/**
 * Attempts to parse a string encoded by `encodeTimeviewBucketDisplay` back to its individual parts.
 * Any pieces that cannot be parsed will return a default value.
 */
export const parseTimeviewBucketDisplay = (bucketDisplay: string): ParseTimeviewBucketDisplayResult => {
  const [type, bucket] = bucketDisplay.split("-");

  return {
    viewType: isTimeviewViewType(type) ? type : DEFAULT_TIMEVIEW_VIEW_TYPE,
    bucketMode: isTimeviewBucketMode(bucket) ? bucket : DEFAULT_TIMEVIEW_BUCKET_MODE,
  };
};

/**
 * A list of `TimeviewBucketDisplay` options to be displayed in a select element.
 */
export const timeviewBucketDisplayOptions: Readonly<{ text: string; value: TimeviewBucketDisplay }[]> = [
  { text: "Percentage - Daily", value: encodeTimeviewBucketDisplay("percent", "daily") },
  { text: "Percentage - Weekly", value: encodeTimeviewBucketDisplay("percent", "weekly") },
  { text: "Percentage - Monthly", value: encodeTimeviewBucketDisplay("percent", "monthly") },
  { text: "Percentage - Quarterly", value: encodeTimeviewBucketDisplay("percent", "quarterly") },
  { text: "Percentage - Yearly", value: encodeTimeviewBucketDisplay("percent", "yearly") },
  { text: "Count - Daily", value: encodeTimeviewBucketDisplay("count", "daily") },
  { text: "Count - Weekly", value: encodeTimeviewBucketDisplay("count", "weekly") },
  { text: "Count - Monthly", value: encodeTimeviewBucketDisplay("count", "monthly") },
  { text: "Count - Quarterly", value: encodeTimeviewBucketDisplay("count", "quarterly") },
  { text: "Count - Yearly", value: encodeTimeviewBucketDisplay("count", "yearly") },
];

/**
 * A list of `TimeviewBucketMode` options to be displayed in a select element.
 */
export const timeviewGrainOptions: Readonly<{ text: string; value: TimeviewBucketMode }[]> = [
  { text: "Daily", value: "daily" },
  { text: "Weekly", value: "weekly" },
  { text: "Monthly", value: "monthly" },
  { text: "Quarterly", value: "quarterly" },
  { text: "Yearly", value: "yearly" },
];

export const buildTimeviewChartFilterInfoSection = (answerFilterText: string, dateFilterText: string) =>
  `${answerFilterText} by ${dateFilterText}`;
